import { 
  collection, 
  addDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  getDocs,
  updateDoc,
  doc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Notification service for social interactions
class NotificationsService {
  // Create a notification
  static async createNotification(userId, type, data) {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId: userId,
        type: type,
        data: data,
        read: false,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error creating notification:', error);
    }
  }

  // Get user notifications
  static async getUserNotifications(userId, limitCount = 20) {
    try {
      const notificationsQuery = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const querySnapshot = await getDocs(notificationsQuery);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      return [];
    }
  }

  // Mark notification as read
  static async markAsRead(notificationId) {
    try {
      await updateDoc(doc(db, 'notifications', notificationId), {
        read: true,
        readAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  }

  // Mark all notifications as read
  static async markAllAsRead(userId) {
    try {
      const notifications = await this.getUserNotifications(userId, 50);
      const unreadNotifications = notifications.filter(n => !n.read);
      
      const updatePromises = unreadNotifications.map(notification => 
        this.markAsRead(notification.id)
      );
      
      await Promise.all(updatePromises);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  }

  // Notification types and their generators
  static async notifyFriendRequest(toUserId, fromUserId, fromUserName) {
    await this.createNotification(toUserId, 'friend_request', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      message: `${fromUserName} sent you a friend request`
    });
  }

  static async notifyFriendAccepted(toUserId, fromUserId, fromUserName) {
    await this.createNotification(toUserId, 'friend_accepted', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      message: `${fromUserName} accepted your friend request`
    });
  }

  static async notifyBookRecommendation(toUserId, fromUserId, fromUserName, bookTitle) {
    await this.createNotification(toUserId, 'book_recommendation', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      bookTitle: bookTitle,
      message: `${fromUserName} recommended "${bookTitle}" to you`
    });
  }

  static async notifyActivityLike(toUserId, fromUserId, fromUserName, activityType) {
    await this.createNotification(toUserId, 'activity_like', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      activityType: activityType,
      message: `${fromUserName} liked your ${activityType}`
    });
  }

  static async notifyActivityComment(toUserId, fromUserId, fromUserName, activityType) {
    await this.createNotification(toUserId, 'activity_comment', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      activityType: activityType,
      message: `${fromUserName} commented on your ${activityType}`
    });
  }

  static async notifyClubInvite(toUserId, fromUserId, fromUserName, clubName) {
    await this.createNotification(toUserId, 'club_invite', {
      fromUserId: fromUserId,
      fromUserName: fromUserName,
      clubName: clubName,
      message: `${fromUserName} invited you to join "${clubName}" book club`
    });
  }

  static async notifyClubDiscussion(memberIds, authorId, authorName, clubName, discussionTitle) {
    const notificationPromises = memberIds
      .filter(id => id !== authorId) // Don't notify the author
      .map(memberId => 
        this.createNotification(memberId, 'club_discussion', {
          authorId: authorId,
          authorName: authorName,
          clubName: clubName,
          discussionTitle: discussionTitle,
          message: `${authorName} started a discussion "${discussionTitle}" in ${clubName}`
        })
      );
    
    await Promise.all(notificationPromises);
  }

  static async notifyClubMeeting(memberIds, organizerId, organizerName, clubName, meetingTitle) {
    const notificationPromises = memberIds
      .filter(id => id !== organizerId) // Don't notify the organizer
      .map(memberId => 
        this.createNotification(memberId, 'club_meeting', {
          organizerId: organizerId,
          organizerName: organizerName,
          clubName: clubName,
          meetingTitle: meetingTitle,
          message: `${organizerName} scheduled a meeting "${meetingTitle}" in ${clubName}`
        })
      );
    
    await Promise.all(notificationPromises);
  }

  // Get notification icon based on type
  static getNotificationIcon(type) {
    const iconMap = {
      'friend_request': '👋',
      'friend_accepted': '🤝',
      'book_recommendation': '📚',
      'activity_like': '❤️',
      'activity_comment': '💬',
      'club_invite': '🏛️',
      'club_discussion': '💬',
      'club_meeting': '📅',
      'reading_goal': '🎯',
      'reading_streak': '🔥',
      'book_milestone': '📖'
    };
    
    return iconMap[type] || '📱';
  }

  // Get notification color based on type
  static getNotificationColor(type) {
    const colorMap = {
      'friend_request': 'var(--color-primary)',
      'friend_accepted': 'var(--color-success)',
      'book_recommendation': 'var(--color-accent)',
      'activity_like': 'var(--color-error)',
      'activity_comment': 'var(--color-secondary)',
      'club_invite': 'var(--color-accent)',
      'club_discussion': 'var(--color-secondary)',
      'club_meeting': 'var(--color-warning)',
      'reading_goal': 'var(--color-success)',
      'reading_streak': 'var(--color-warning)',
      'book_milestone': 'var(--color-primary)'
    };
    
    return colorMap[type] || 'var(--color-text-secondary)';
  }
}

export default NotificationsService;